import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getCalculatorContent } from 'spw-components/growth-calculator/calculator_selector';
import { CalculatorType } from 'spw-redux/modules/calculators/types';
import { IAppState } from 'spw-redux/reducer';
import Worksheet from 'spw-components/worksheet/programming';
import SaveScenarioDialog from 'spw-components/dialogs/save-scenario/dialog';
import {
  setCurrentScenario,
  setIsDeleteConfirmationOpen,
} from 'spw-redux/modules/calculators/actions';
import { getDepartmentsByProjectId } from 'spw-redux/modules/projects/actions';
import { SPWExportButton } from 'spw-shared/globalComponents/buttons';
import { JustifyEndRow } from 'spw-shared/globalComponents/grid';
import * as GA from 'spw-shared/analytics';
import { DEFAULT_STREET_ADDRESS, REQUEST_STATUS } from 'spw-constants';
import { ShowHideDecimalToggle } from 'spw-components/global/ShowHideDecimalToggle';
import {
  selectCurrentScenario,
  selectScenarios,
  selectScenariosRequestStatus,
  selectTotalPrograms,
  selectTotalProgramsRequestStatus,
} from 'spw-redux/modules/calculators/selectors';
import ConfirmWorksheetUpdate from 'spw-components/dialogs/confirm-worksheet-update';
import { getZipNamePrefix, sanitizeFileName } from 'spw-shared/utils';
import { prepareAndExecuteReportDownload } from 'spw-redux/modules/downloads/actions';
import { getZipNameOrExportablePrefix } from 'spw-redux/modules/downloads/utils';
import {
  selectIndividualReportZipName,
  selectIsDownloadingIndividualReport,
} from 'spw-redux/modules/downloads/selectors';
import { REPORT_TYPES } from 'spw-redux/modules/reports/constants';
import { ReportAttributes, PNG } from 'spw-redux/modules/reports/types';
import { useParams } from 'react-router';
import { isDepartmentScenario } from 'spw-components/worksheet/utils';
import { selectClientFacingNamesAreVisible } from 'spw-redux/modules/space_types/selectors';
import {
  currentProject as selectCurrentProject,
  selectFirstBuilding,
  selectProjectDepartments,
} from 'spw-redux/modules/projects/selectors';
import { currentUser } from 'spw-redux/modules/user/selectors';
import { useScenarioFloor } from 'spw-programming-projects/hooks';
import Header from './header';
import { CalculatorActionsWrapper, Wrapper } from './styles';
import USFBuildingCalculations from './USF-building-calculations';
import DeleteScenarioDialog from '../delete-scenario-dialog';
import { PlainProgramScenario } from '../../../models/Scenario';

const WORKSHEET_COLLAPSED_HEIGHT = 142;

interface IBuildingHCSCScenario {
  isDeleteConfirmationOpen: boolean;
  isEditingSavedScenario: boolean;
  showDialog: boolean;
  isRenderingForExport?: boolean;
}

export const BuildingHCSCScenario: React.FC<IBuildingHCSCScenario> = (
  props: IBuildingHCSCScenario
) => {
  const {
    isDeleteConfirmationOpen = false,
    isEditingSavedScenario,
    showDialog,
    isRenderingForExport,
  } = props;

  const user = useSelector(currentUser);
  const project = useSelector(selectCurrentProject);
  const building = useSelector(selectFirstBuilding);
  const currentScenario = useSelector(selectCurrentScenario);
  const departments = useSelector(selectProjectDepartments);
  const scenariosRequestStatus = useSelector(selectScenariosRequestStatus);
  const totalProgramsRequestStatus = useSelector(
    selectTotalProgramsRequestStatus
  );
  const clientFacingNamesAreVisible = useSelector(
    selectClientFacingNamesAreVisible
  );
  const scenarios = useSelector(selectScenarios, shallowEqual);
  const totalPrograms = useSelector(selectTotalPrograms);
  const dispatch = useDispatch();
  const routeParams: {
    scenarioId?: string;
    totalProgramId?: string;
  } = useParams();
  const scenarioId = parseInt(routeParams?.scenarioId || '0', 10);
  const totalProgramId = parseInt(routeParams?.totalProgramId || '0', 10);

  useEffect(() => {
    if (
      !currentScenario?.id &&
      scenariosRequestStatus === REQUEST_STATUS.SUCCESS &&
      totalProgramsRequestStatus === REQUEST_STATUS.SUCCESS &&
      (scenarios.length || totalPrograms.length)
    ) {
      let scenario;

      if (totalProgramId) {
        scenario = totalPrograms?.find((s) => s.id === totalProgramId);
      } else {
        scenario = scenarios.find((s) => s.id === scenarioId);
      }

      if (scenario) {
        if (
          (isDepartmentScenario(scenario as PlainProgramScenario) ||
            totalProgramId) &&
          (!departments || departments?.length === 0)
        ) {
          dispatch(getDepartmentsByProjectId(project.id));
        }
        dispatch(setCurrentScenario(scenario));
      }
    }
  }, [
    dispatch,
    scenarioId,
    totalProgramId,
    currentScenario?.id,
    project?.id,
    scenariosRequestStatus,
    totalProgramsRequestStatus,
    totalPrograms,
    scenarios,
    departments,
  ]);

  useScenarioFloor();

  const [showDecimals, setShowDecimals] = useState(false);
  const isDownloadingIndividualReport = useSelector(
    selectIsDownloadingIndividualReport
  );

  const individualReportZipName = useSelector(selectIndividualReportZipName);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (isEditingSavedScenario) {
      setShowDecimals(false);
    }
  }, [isEditingSavedScenario]);

  const toggleShowDecimals = React.useCallback(
    () => setShowDecimals((prev) => !prev),
    [setShowDecimals]
  );

  const setConfirmationOpen = (isOpen: boolean) =>
    dispatch(setIsDeleteConfirmationOpen(isOpen));

  // TODO: Move to the backend
  const zipName = useMemo(() => {
    if (!currentScenario) return '';
    return `${
      building?.streetAddress &&
      building.streetAddress !== DEFAULT_STREET_ADDRESS
        ? `${building?.streetAddress}_`
        : ''
    }${currentScenario?.name}_`
      .concat(
        getZipNamePrefix(
          currentScenario,
          getZipNameOrExportablePrefix(currentScenario, building)
        )
      )
      .concat('HC Summary.zip');
  }, [building, currentScenario]);

  const handleExport = useCallback(() => {
    GA.event({
      category: 'Programming',
      subCategory: GA.dupCategoryFromCalculator(
        currentScenario?.calculatorType
      ),
      action: GA.tagFromExportCalculator(currentScenario?.calculatorType),
      label: JSON.stringify({
        projectType: 'programming',
        userId: user.id,
      }),
    });

    dispatch(
      prepareAndExecuteReportDownload(
        {
          reportType: REPORT_TYPES.SEAT_COUNT_ANALYSIS,
          entityId: currentScenario.id,
          format: PNG,
        } as ReportAttributes,
        zipName
      )
    );
  }, [currentScenario, user, zipName, dispatch]);

  const calculatorActions = useMemo(
    () => (
      <CalculatorActionsWrapper
        style={{
          display: isRenderingForExport ? 'none' : 'flex',
        }}
      >
        <JustifyEndRow>
          {!isRenderingForExport && !isEditingSavedScenario && (
            <ShowHideDecimalToggle
              showDecimals={showDecimals}
              toggleShowDecimals={toggleShowDecimals}
              labelPlacement="start"
              testingId="scenario-decimals-toggle"
            />
          )}
          <div className="download-button-wrapper">
            <SPWExportButton
              busy={
                isDownloadingIndividualReport &&
                sanitizeFileName(zipName) === individualReportZipName
              }
              onClick={isDownloadingIndividualReport ? undefined : handleExport}
              disabled={isEditingSavedScenario}
            />
          </div>
        </JustifyEndRow>
      </CalculatorActionsWrapper>
    ),
    [
      isRenderingForExport,
      isEditingSavedScenario,
      showDecimals,
      toggleShowDecimals,
      handleExport,
      individualReportZipName,
      isDownloadingIndividualReport,
      zipName,
    ]
  );

  if (!currentScenario) return null;

  const calculatorContent = getCalculatorContent(
    currentScenario?.calculatorType,
    {
      scenario: currentScenario,
      building,
      isEditingSavedScenario,
      calculatorType: currentScenario?.calculatorType.replace(
        /_/g,
        '-'
      ) as CalculatorType,
      departments,
      project,
      totalPrograms,
      showDecimals,
      isRenderingForExport,
      calculatorActions,
      disableHighlighting: true,
    }
  );

  return (
    <Wrapper
      paddingBottom={isRenderingForExport ? 0 : WORKSHEET_COLLAPSED_HEIGHT}
    >
      {!isRenderingForExport && <Header />}
      {!isRenderingForExport && showDialog && <SaveScenarioDialog />}
      {!isRenderingForExport && <ConfirmWorksheetUpdate />}
      {!isRenderingForExport && (
        <DeleteScenarioDialog
          isOpen={isDeleteConfirmationOpen}
          onClose={() => setConfirmationOpen(false)}
        />
      )}
      {calculatorContent}
      {!isRenderingForExport && <USFBuildingCalculations />}
      {!isRenderingForExport && (
        <Worksheet clientFacingNamesAreVisible={clientFacingNamesAreVisible} />
      )}
    </Wrapper>
  );
};

const mapStateToProps = (state: IAppState) => ({
  isDeleteConfirmationOpen: state.calculatorReducer.isDeleteConfirmationOpen,
  isEditingSavedScenario: state.calculatorReducer.isEditingSavedScenario,
  showDialog: state.calculatorReducer.showScenarioDialog,
});

export default connect(mapStateToProps)(BuildingHCSCScenario);
